import { FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { User } from 'firebase/auth';
import {
  auth,
  onAuthChange,
  loginWithEmail,
  signUpWithEmail,
  logout,
  resendEmailVerification,
  refreshCurrentUserToken,
  consumeBrandInvite,
} from '../services/firebase';
import { Button } from '../components/common/Button';
import { TrademarkNotice } from '../components/common/TrademarkNotice';

/**
 * Brand Portal magic-link landing page (Phase 1.3b) — route
 * `/brand-portal/accept-invite?token=…`. Standalone (no admin nav chrome) and
 * reachable while signed out.
 *
 * Flow:
 *  - signed out → welcome card with sign-in / create-account paths
 *  - just signed up → "check your email" screen (verification gate)
 *  - signed in → auto-consume the invite, then bounce to the brand dashboard
 *
 * After a successful consume the new `brandMemberships` entry only exists on the
 * server, so we redirect with a FULL page load (`window.location.assign`) — that
 * re-runs the auth/role check from scratch and the dashboard's membership gate
 * passes. A client-side navigate would still see the stale membership and bounce.
 */

// ---------------------------------------------------------------------------
// Layout shell
// ---------------------------------------------------------------------------

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-brand-50 to-brand-100 px-4">
      <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md">
        {children}
      </div>
      <TrademarkNotice className="mt-6 max-w-md" />
    </div>
  );
}

function CenteredSpinner({ label }: { label: string }) {
  return (
    <Shell>
      <div className="text-center py-6">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600 mx-auto" />
        <p className="mt-4 text-gray-600">{label}</p>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Consume-error classification
// ---------------------------------------------------------------------------

type ConsumeErrorKind = 'verify' | 'wrong-account' | 'invalid' | 'unknown';

interface ParsedConsumeError {
  kind: ConsumeErrorKind;
  message: string;
  inviteEmail?: string;
}

function parseConsumeError(err: unknown): ParsedConsumeError {
  const message =
    err instanceof Error ? err.message : 'Something went wrong accepting the invite.';
  const lower = message.toLowerCase();
  const details = (err as { details?: { email?: string } })?.details;
  const inviteEmail = details?.email;

  if (lower.includes('verify') || lower.includes('verified')) {
    return { kind: 'verify', message, inviteEmail };
  }
  if (
    lower.includes("isn't for your account") ||
    lower.includes('not for your account') ||
    lower.includes('different account') ||
    lower.includes('another account')
  ) {
    return { kind: 'wrong-account', message, inviteEmail };
  }
  if (
    lower.includes('expired') ||
    lower.includes('no longer valid') ||
    lower.includes('not found') ||
    lower.includes('invalid') ||
    lower.includes('already')
  ) {
    return { kind: 'invalid', message, inviteEmail };
  }
  return { kind: 'unknown', message, inviteEmail };
}

// ---------------------------------------------------------------------------
// Signed-out: sign in / create account
// ---------------------------------------------------------------------------

function SignedOutView({
  onSignedUp,
}: {
  /** Called after a successful create-account so the parent shows the verify screen. */
  onSignedUp: (email: string) => void;
}) {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await loginWithEmail(email.trim(), password);
      // onAuthChange in the parent takes over → auto-consume.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
      setSubmitting(false);
    }
  };

  const handleSignUp = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const trimmed = email.trim();
      await signUpWithEmail(trimmed, password);
      onSignedUp(trimmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign up failed');
      setSubmitting(false);
    }
  };

  return (
    <Shell>
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          You&apos;ve been invited to Watch Keeper®
        </h1>
        <p className="text-gray-500 mt-2">
          Sign in or create an account to accept your invite.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
        <button
          type="button"
          onClick={() => {
            setMode('signin');
            setError(null);
          }}
          className={`flex-1 text-sm font-medium py-1.5 rounded-md transition-colors ${
            mode === 'signin'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Sign in
        </button>
        <button
          type="button"
          onClick={() => {
            setMode('signup');
            setError(null);
          }}
          className={`flex-1 text-sm font-medium py-1.5 rounded-md transition-colors ${
            mode === 'signup'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Create account
        </button>
      </div>

      <form
        onSubmit={mode === 'signin' ? handleSignIn : handleSignUp}
        className="space-y-4"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            placeholder="you@example.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            placeholder="********"
          />
        </div>
        {mode === 'signup' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm password
            </label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              placeholder="********"
            />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <Button type="submit" className="w-full" loading={submitting}>
          {mode === 'signin' ? 'Sign in' : 'Create account'}
        </Button>
      </form>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Just signed up: verify-email gate
// ---------------------------------------------------------------------------

function AccountCreatedView({ email }: { email: string }) {
  return (
    <Shell>
      <div className="text-center">
        <div className="text-5xl mb-4">📬</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Account created</h1>
        <p className="text-gray-600 mb-6">
          We&apos;ve sent a verification link to{' '}
          <span className="font-medium text-gray-900">{email}</span>. Click it,
          then come back here to accept your invite.
        </p>
        <Button className="w-full" onClick={() => window.location.reload()}>
          Continue
        </Button>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Signed in: consume the invite
// ---------------------------------------------------------------------------

function ConsumeView({
  token,
  signedInEmail,
}: {
  token: string;
  signedInEmail: string | null;
}) {
  const [phase, setPhase] = useState<'accepting' | 'success' | 'error'>(
    'accepting',
  );
  const [brandName, setBrandName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedConsumeError | null>(null);
  const [working, setWorking] = useState(false);

  const run = async () => {
    setPhase('accepting');
    setParsed(null);
    try {
      const res = await consumeBrandInvite(token);
      setBrandName(res.brandName ?? null);
      setPhase('success');
      // Full reload so the role check re-runs and the dashboard membership gate
      // sees the freshly-granted membership.
      window.setTimeout(() => {
        window.location.assign(`/brand-portal/${res.brandId}`);
      }, 1200);
    } catch (err) {
      console.error('consumeBrandInvite failed', err);
      setParsed(parseConsumeError(err));
      setPhase('error');
    }
  };

  // Auto-consume on mount (and whenever the token changes).
  useEffect(() => {
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (phase === 'accepting') {
    return <CenteredSpinner label="Accepting invite…" />;
  }

  if (phase === 'success') {
    return (
      <Shell>
        <div className="text-center py-4">
          <div className="text-5xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome to {brandName ?? 'your brand'}!
          </h1>
          <p className="text-gray-600">Taking you to your dashboard…</p>
        </div>
      </Shell>
    );
  }

  // phase === 'error'
  const kind = parsed?.kind ?? 'unknown';

  if (kind === 'verify') {
    return (
      <Shell>
        <div className="text-center">
          <div className="text-5xl mb-4">✉️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Verify your email first
          </h1>
          <p className="text-gray-600 mb-6">
            We&apos;ve sent a verification link to{' '}
            <span className="font-medium text-gray-900">{signedInEmail}</span>.
            Click the link in your inbox, then retry.
          </p>
          <div className="space-y-3">
            <Button
              className="w-full"
              loading={working}
              onClick={async () => {
                setWorking(true);
                try {
                  await refreshCurrentUserToken();
                  await run();
                } finally {
                  setWorking(false);
                }
              }}
            >
              I&apos;ve verified — retry
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              disabled={working}
              onClick={async () => {
                try {
                  await resendEmailVerification();
                } catch {
                  /* surfaced below via the generic note */
                }
              }}
            >
              Resend verification email
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  if (kind === 'wrong-account') {
    return (
      <Shell>
        <div className="text-center">
          <div className="text-5xl mb-4">🔁</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Wrong account
          </h1>
          <p className="text-gray-600 mb-6">
            {parsed?.inviteEmail ? (
              <>
                This invite was sent to{' '}
                <span className="font-medium text-gray-900">
                  {parsed.inviteEmail}
                </span>
                , but you&apos;re signed in as{' '}
              </>
            ) : (
              <>This invite isn&apos;t for the account you&apos;re signed in as: </>
            )}
            <span className="font-medium text-gray-900">{signedInEmail}</span>.
            Sign out and sign in with the right account, or contact whoever
            invited you.
          </p>
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => logout()}
          >
            Sign out
          </Button>
        </div>
      </Shell>
    );
  }

  // invalid / unknown
  return (
    <Shell>
      <div className="text-center">
        <div className="text-5xl mb-4">⚠️</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          This invite is no longer valid
        </h1>
        <p className="text-gray-600 mb-6">
          {kind === 'invalid'
            ? 'Ask whoever invited you to send a new one.'
            : parsed?.message}
        </p>
        <Button variant="secondary" className="w-full" onClick={() => logout()}>
          Sign out
        </Button>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [justSignedUpEmail, setJustSignedUpEmail] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const unsub = onAuthChange((u) => {
      setFirebaseUser(u);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  if (!authReady) {
    return <CenteredSpinner label="Loading…" />;
  }

  if (!token) {
    return (
      <Shell>
        <div className="text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Invite link incomplete
          </h1>
          <p className="text-gray-600">
            This link is missing its invite token. Ask whoever invited you to
            send a fresh link.
          </p>
        </div>
      </Shell>
    );
  }

  // Right after self-signup we hold the verify screen even though the user is
  // now technically signed in (Firebase signs them in on account creation).
  if (justSignedUpEmail) {
    return <AccountCreatedView email={justSignedUpEmail} />;
  }

  if (!firebaseUser) {
    return <SignedOutView onSignedUp={setJustSignedUpEmail} />;
  }

  return (
    <ConsumeView token={token} signedInEmail={firebaseUser.email ?? auth.currentUser?.email ?? null} />
  );
}
